import { useState, useEffect, FormEvent } from 'react';
import { Mailbox, RefreshCw, Pencil, Trash2, PlayCircle, Loader2 } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { api } from '../../lib/api';
import ConfirmDialog from './ConfirmDialog';

interface AdminMailAccount {
  id: number;
  label: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  active: boolean;
  ownerUserId: number;
  ownerEmail: string | null;
  teamOwnerEmail: string | null;
  teamId: number | null;
  teamName: string | null;
  lastTestStatus: 'ok' | 'failed' | null;
  lastTestError: string | null;
  lastTestedAt: string | null;
  createdAt: string;
}

interface EditForm {
  id: number;
  label: string;
  host: string;
  port: string;
  secure: boolean;
  username: string;
  password: string;
}

export default function PostfaecherTab() {
  const { darkMode } = useDarkMode();
  const [accounts, setAccounts] = useState<AdminMailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editing, setEditing] = useState<EditForm | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<AdminMailAccount | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get<AdminMailAccount[]>('/api/mail-monitor/admin/all');
      setAccounts(data);
    } catch (e: any) {
      setError(e.message || 'Konnte Postfächer nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleTest = async (acc: AdminMailAccount) => {
    setBusyId(acc.id);
    try {
      const updated = await api.post<AdminMailAccount>(`/api/mail-monitor/${acc.id}/test`, {});
      setAccounts(list => list.map(a => (a.id === acc.id ? { ...a, ...updated } : a)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (acc: AdminMailAccount) => {
    setBusyId(acc.id);
    try {
      const updated = await api.patch<AdminMailAccount>(`/api/mail-monitor/${acc.id}/active`, { active: !acc.active });
      setAccounts(list => list.map(a => (a.id === acc.id ? { ...a, ...updated } : a)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    const acc = deleteCandidate;
    setBusyId(acc.id);
    setDeleteCandidate(null);
    try {
      await api.delete(`/api/mail-monitor/${acc.id}`);
      setAccounts(list => list.filter(a => a.id !== acc.id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (acc: AdminMailAccount) => {
    setEditing({
      id: acc.id,
      label: acc.label,
      host: acc.host,
      port: String(acc.port),
      secure: acc.secure,
      username: acc.username,
      password: '',
    });
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSubmitting(true);
    setError('');
    try {
      const body: Record<string, unknown> = {
        label: editing.label.trim(),
        host: editing.host.trim(),
        port: Number(editing.port),
        secure: editing.secure,
        username: editing.username.trim(),
      };
      if (editing.password.length > 0) body.password = editing.password;
      const updated = await api.patch<AdminMailAccount>(`/api/mail-monitor/${editing.id}`, body);
      setAccounts(list => list.map(a => (a.id === editing.id ? { ...a, ...updated } : a)));
      setEditing(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const input = `w-full px-3 py-2 rounded-md border text-sm ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`;

  return (
    <div className={`rounded-xl border ${card} p-5`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Mailbox className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Postfächer (alle Teams)</h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              IMAP-Konfigurationen aller Teams. Du kannst sie als Admin bearbeiten, testen und deaktivieren.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 shrink-0 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Neu laden
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Lade…</p>
      ) : accounts.length === 0 ? (
        <p className="text-sm text-slate-500">Noch keine Postfächer im System eingerichtet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              <tr className="text-left text-xs uppercase tracking-wide">
                <th className="py-2 pr-3">Team</th>
                <th className="py-2 pr-3">Team-Inhaber</th>
                <th className="py-2 pr-3">Eingerichtet von</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Host</th>
                <th className="py-2 pr-3">Username</th>
                <th className="py-2 pr-3">Test</th>
                <th className="py-2 pr-3">Aktiv</th>
                <th className="py-2 pl-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
              {accounts.map(acc => (
                <tr key={acc.id}>
                  <td className="py-2 pr-3">{acc.teamName ?? <span className="text-slate-400">— (Team {acc.teamId ?? '?'})</span>}</td>
                  <td className="py-2 pr-3 text-xs">{acc.teamOwnerEmail ?? <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 pr-3 text-xs">{acc.ownerEmail ?? <span className="text-slate-400">—</span>}</td>
                  <td className="py-2 pr-3 font-medium">{acc.label}</td>
                  <td className="py-2 pr-3 text-xs">{acc.host}:{acc.port}</td>
                  <td className="py-2 pr-3 text-xs">{acc.username}</td>
                  <td className="py-2 pr-3 text-xs">
                    {acc.lastTestStatus === 'ok' && <span className="text-emerald-600">✓ ok</span>}
                    {acc.lastTestStatus === 'failed' && <span className="text-red-600" title={acc.lastTestError ?? ''}>✗ failed</span>}
                    {!acc.lastTestStatus && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      checked={acc.active}
                      disabled={busyId === acc.id}
                      onChange={() => handleToggle(acc)}
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="py-2 pl-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => handleTest(acc)}
                        disabled={busyId === acc.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                      >
                        {busyId === acc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                        Test
                      </button>
                      <button
                        onClick={() => openEdit(acc)}
                        disabled={busyId === acc.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                      >
                        <Pencil className="w-3 h-3" />
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => setDeleteCandidate(acc)}
                        disabled={busyId === acc.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/20 disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitEdit} className={`${card} rounded-xl border shadow-xl max-w-md w-full p-5`}>
            <h3 className="font-semibold mb-3">Postfach bearbeiten</h3>
            <div className="space-y-2.5">
              <label className="block text-xs font-medium">Label
                <input className={input} value={editing.label} onChange={e => setEditing(f => f && { ...f, label: e.target.value })} required />
              </label>
              <label className="block text-xs font-medium">Host
                <input className={input} value={editing.host} onChange={e => setEditing(f => f && { ...f, host: e.target.value })} required />
              </label>
              <div className="flex gap-2">
                <label className="flex-1 text-xs font-medium">Port
                  <input type="number" className={input} value={editing.port} onChange={e => setEditing(f => f && { ...f, port: e.target.value })} required />
                </label>
                <label className="inline-flex items-center gap-2 mt-5 text-xs">
                  <input type="checkbox" checked={editing.secure} onChange={e => setEditing(f => f && { ...f, secure: e.target.checked })} className="rounded border-slate-300" />
                  TLS
                </label>
              </div>
              <label className="block text-xs font-medium">Username
                <input className={input} value={editing.username} onChange={e => setEditing(f => f && { ...f, username: e.target.value })} required />
              </label>
              <label className="block text-xs font-medium">Passwort <span className="font-normal text-slate-500">(leer lassen = unverändert)</span>
                <input type="password" className={input} value={editing.password} onChange={e => setEditing(f => f && { ...f, password: e.target.value })} autoComplete="new-password" />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setEditing(null)} disabled={submitting} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-50">
                Abbrechen
              </button>
              <button type="submit" disabled={submitting} className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50">
                {submitting ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={deleteCandidate !== null}
        title="Postfach löschen?"
        message={`„${deleteCandidate?.label}" (${deleteCandidate?.username}) wird gelöscht. Dies kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        onConfirm={handleDelete}
        onCancel={() => setDeleteCandidate(null)}
      />
    </div>
  );
}
